import { useState, useEffect } from 'react';
import { Building2, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { useCopy, fill } from '../../i18n';
import { getPendingInvitesForUser, acceptInvite, declineInvite } from '../../services/organizationService';
import { useOrganization } from '../../contexts/OrganizationContext';
import { toast } from 'sonner';
import type { OrganizationInvite, Organization } from '../../types';

type InviteWithOrg = OrganizationInvite & { organization?: Organization };

export default function OrgInviteBanner() {
  const { user, profile } = useAuth();
  const t = useCopy().account.org.inviteBanner;
  const { refreshOrganizations } = useOrganization();
  const [invites, setInvites] = useState<InviteWithOrg[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    async function loadInvites() {
      if (!profile?.email) return;
      try {
        const pending = await getPendingInvitesForUser(profile.email);
        setInvites(pending);
      } catch {
        // silently ignore
      }
    }
    loadInvites();
  }, [profile?.email]);

  const handleAccept = async (invite: InviteWithOrg) => {
    setActionLoading(invite.id);
    try {
      await acceptInvite(invite.token);
      setInvites(prev => prev.filter(i => i.id !== invite.id));
      await refreshOrganizations();
      toast.success(fill(t.joined, { org: invite.organization?.name || t.orgFallback }));
    } catch (err) {
      // 'invite_expired' e 'org_member_limit_reached' são códigos do serviço.
      const message = err instanceof Error ? err.message : t.acceptError;
      if (message === 'invite_expired') {
        toast.error(t.expired);
        setInvites(prev => prev.filter(i => i.id !== invite.id));
      } else if (message === 'org_member_limit_reached') {
        toast.error(t.memberLimit);
      } else {
        toast.error(message);
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (invite: InviteWithOrg) => {
    setActionLoading(invite.id);
    try {
      await declineInvite(invite.token);
      setInvites(prev => prev.filter(i => i.id !== invite.id));
    } catch {
      toast.error(t.declineError);
    } finally {
      setActionLoading(null);
    }
  };

  if (invites.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 px-3 py-2">
      {invites.map(invite => (
        <div
          key={invite.id}
          className="flex items-center gap-2 p-2.5 rounded-lg bg-[var(--color-primary)]/5 border border-[var(--color-primary)]/20"
        >
          <Building2 size={16} className="text-[var(--color-primary)] shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-[var(--color-text)] truncate">
              {fill(t.inviteFor, { org: invite.organization?.name || t.orgFallback })}
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {actionLoading === invite.id ? (
              <Loader2 size={14} className="animate-spin text-[var(--color-text-muted)]" />
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDecline(invite)}
                  className="h-6 w-6 p-0"
                  title={t.decline}
                >
                  <X size={12} />
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleAccept(invite)}
                  className="h-6 px-2 text-[10px]"
                >
                  <Check size={10} className="mr-0.5" />
                  {t.accept}
                </Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
